import { Component, Inject, Input, Optional } from '@angular/core';
import type { ValidateFileResponse } from '../../../../API';
import { parseJsonField } from '../../../shared/utils/json.util';
import { getFileNameFromS3Key } from '../../../shared/utils/upload-file.util';
import type {
  ColumnErrorDetails,
  ColumnErrorViewModel,
  ErrorTypeDetails,
  ErrorTypeViewModel,
  FileValidationLog,
  RowValidationDetails,
  ValidationLogsInput,
  ValidationReportViewModel,
  ValidationSummary,
} from '../../models/validation-logs';
import { NZ_MODAL_DATA } from 'ng-zorro-antd/modal';

@Component({
  selector: 'app-validation-logs',
  templateUrl: './validation-logs.component.html',
  styleUrl: './validation-logs.component.scss',
})
export class ValidationLogsComponent {
  reports: ValidationReportViewModel[] = [];
  fallbackText = '';

  @Input() set logs(value: ValidationLogsInput) {
    console.log('value', value);
    this.reports = this.buildReports(value);
    this.fallbackText = this.getFallbackText(value);
  }

  constructor(
    @Optional()
    @Inject(NZ_MODAL_DATA)
    private modalData: { logs: ValidationLogsInput },
  ) {}

  ngOnInit(): void {
    if (this.modalData?.logs) {
      this.reports = this.buildReports(this.modalData?.logs);
      this.fallbackText = this.getFallbackText(this.modalData?.logs);
    }
  }

  get hasLogs(): boolean {
    return this.reports.length > 0 || this.fallbackText.length > 0;
  }

  private buildReports(value: ValidationLogsInput): ValidationReportViewModel[] {
    const entries = Array.isArray(value) ? value : [value];

    return entries
      .map((entry) => this.normalizeEntry(entry))
      .filter((entry): entry is FileValidationLog => !!entry)
      .map((entry, index) => this.toReportViewModel(entry, index));
  }

  private normalizeEntry(
    entry: ValidationLogsInput | ValidateFileResponse | FileValidationLog,
  ): FileValidationLog | null {
    if (!entry) {
      return null;
    }

    if (typeof entry === 'string') {
      const parsed = parseJsonField(entry);
      return typeof parsed === 'object' && parsed !== null ? this.normalizeEntry(parsed as ValidateFileResponse) : null;
    }

    if (this.isFileValidationLog(entry)) {
      return entry;
    }

    if (this.isValidateFileResponse(entry)) {
      return { result: entry };
    }

    return null;
  }

  private toReportViewModel(entry: FileValidationLog, index: number): ValidationReportViewModel {
    const result = entry.result;
    const summary = parseJsonField(result.summary) as ValidationSummary;
    const rowValidation = parseJsonField(result.rowValidation) as RowValidationDetails;
    const totalColumns = result.matchedColumns.length + result.missingColumns.length + result.extraColumns.length;
    const matchPercent = totalColumns > 0 ? Math.round((result.matchedColumns.length / totalColumns) * 100) : 0;
    const status = result.isValid ? 'PASSED' : summary.validationStatus || 'FAILED';

    return {
      fileName: entry.fileName || getFileNameFromS3Key(result.s3Key) || `Validation ${index + 1}`,
      status,
      statusTone: result.isValid ? 'success' : 'error',
      summary,
      rowValidation,
      matchedColumns: result.matchedColumns,
      missingColumns: result.missingColumns,
      extraColumns: result.extraColumns,
      totalColumns,
      matchPercent,
      totalErrors: rowValidation.totalErrors ?? 0,
      errorTypes: this.toErrorTypeViewModels(rowValidation.errorsByType),
      columnErrors: this.toColumnErrorViewModels(rowValidation.columnErrors),
      orgId: result.orgId,
      s3Key: result.s3Key,
      typeName: result.__typename,
      rawResponse: JSON.stringify(result, null, 2),
    };
  }

  private toErrorTypeViewModels(errorsByType: Record<string, ErrorTypeDetails> | undefined): ErrorTypeViewModel[] {
    return Object.entries(errorsByType ?? {})
      .map(([name, details]) => ({
        name,
        count: details.count ?? 0,
        affectedColumns: details.affectedColumns ?? [],
        sampleRows: details.sampleRows ?? [],
      }))
      .sort((left, right) => right.count - left.count);
  }

  private toColumnErrorViewModels(
    columnErrors: Record<string, ColumnErrorDetails> | undefined,
  ): ColumnErrorViewModel[] {
    return Object.entries(columnErrors ?? {})
      .map(([column, details]) => ({
        column,
        errorCount: details.errorCount ?? 0,
        errorTypes: details.errorTypes ?? [],
        sampleValues: details.sampleValues ?? [],
      }))
      .sort((left, right) => right.errorCount - left.errorCount);
  }

  private getFallbackText(value: ValidationLogsInput): string {
    if (!value || this.reports.length > 0) {
      return '';
    }

    return typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  }

  private isFileValidationLog(value: object): value is FileValidationLog {
    return 'result' in value && this.isValidateFileResponse((value as FileValidationLog).result);
  }

  private isValidateFileResponse(value: unknown): value is ValidateFileResponse {
    if (!value || typeof value !== 'object') {
      return false;
    }

    const candidate = value as Partial<ValidateFileResponse>;
    return (
      Array.isArray(candidate.extraColumns) &&
      typeof candidate.isValid === 'boolean' &&
      Array.isArray(candidate.matchedColumns) &&
      Array.isArray(candidate.missingColumns) &&
      typeof candidate.orgId === 'string' &&
      typeof candidate.rowValidation === 'string' &&
      typeof candidate.s3Key === 'string' &&
      typeof candidate.summary === 'string'
    );
  }
}
